import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { IncidentType } from '@/types';

import { useFilteredIncidents } from '@/hooks/use-filtered-incidents';
import { useGetIncidentListData } from '@/hooks/use-get-incident-list-data';
import useIncidentSearch from '@/hooks/use-incident-search';
import getIncidentLanguage from '@/utils/helpers/get-incident-language';
import isIncidentMatchedByRoutes from '@/utils/helpers/is-incident-matched-by-routes';
import isIncidentMatchedByStations from '@/utils/helpers/is-incident-matched-by-stations';

interface UseIncidentsListParams {
  selectedRoutes: { journeyId: string; lineNumber: string }[];
  selectedStations: string[];
}

export const useIncidentsList = ({ selectedRoutes, selectedStations }: UseIncidentsListParams) => {
  const { data: allIncidents = [], isLoading, isError, refetch } = useGetIncidentListData();
  const { searchKey } = useIncidentSearch();
  const { i18n } = useTranslation();
  const incidentLang = getIncidentLanguage(i18n.language);

  const searchFiltered = useFilteredIncidents(allIncidents, searchKey, incidentLang);

  const filteredIncidents = useMemo(() => {
    const hasRouteFilters = !!selectedRoutes?.length;
    const hasStationFilters = !!selectedStations?.length;
    const hasActiveFilters = hasRouteFilters || hasStationFilters;

    if (!hasActiveFilters) {
      return searchFiltered;
    }

    const selectedLineNumbers = new Set(selectedRoutes.map((route) => route.lineNumber));
    const selectedStationIds = new Set(selectedStations);

    return searchFiltered.filter((incident: IncidentType) => {
      const matchedRoute =
        hasRouteFilters && isIncidentMatchedByRoutes(incident, selectedLineNumbers);
      const matchedStation =
        hasStationFilters && isIncidentMatchedByStations(incident, selectedStationIds);

      return matchedRoute || matchedStation;
    });
  }, [searchFiltered, selectedRoutes, selectedStations]);

  const isEmpty = !filteredIncidents?.length;

  return { filteredIncidents, isLoading, isEmpty, isError, refetch };
};
